<template>
  <div v-loading="loading" class="page">
    <el-alert
      class="mb-16px"
      type="info"
      show-icon
      :closable="false"
      title="结算流程：平台归集 → 财务审核 → 银行打款 → 您确认到账。状态为「已打款」时可确认或发起异议。"
    />

    <el-row :gutter="12" class="mb-16px">
      <el-col :xs="12" :md="8" v-for="item in kpis" :key="item.label">
        <div class="kpi">
          <div class="kpi__value">{{ item.value }}</div>
          <div class="kpi__label">{{ item.label }}</div>
        </div>
      </el-col>
    </el-row>

    <el-card shadow="never" class="panel">
      <template #header>
        <div class="panel-head">
          <span>我的结算单</span>
          <el-button @click="load"><Icon icon="ep:refresh" class="mr-4px" />刷新</el-button>
        </div>
      </template>
      <el-table :data="list" stripe>
        <el-table-column label="结算单号" prop="settlementNo" min-width="160" />
        <el-table-column label="账期" prop="period" width="100" />
        <el-table-column label="分润合计" width="120">
          <template #default="{ row }">
            <span class="profit">¥{{ fen2yuan(row.totalAmount) }}</span>
          </template>
        </el-table-column>
        <el-table-column label="状态" width="120">
          <template #default="{ row }">
            <dict-tag :type="DICT_TYPE.AGENT_CHANNEL_SETTLE_STATUS" :value="row.status" />
          </template>
        </el-table-column>
        <el-table-column label="打款凭证" prop="payVoucherNo" min-width="140">
          <template #default="{ row }">{{ row.payVoucherNo || '-' }}</template>
        </el-table-column>
        <el-table-column label="打款时间" prop="payTime" min-width="170">
          <template #default="{ row }">{{ row.payTime || '-' }}</template>
        </el-table-column>
        <el-table-column label="创建时间" prop="createTime" min-width="170" />
        <el-table-column label="操作" width="180" fixed="right">
          <template #default="{ row }">
            <template v-if="row.status === 2">
              <el-button link type="success" @click="confirm(row)">确认到账</el-button>
              <el-button link type="danger" @click="openDispute(row)">异议</el-button>
            </template>
            <span v-else-if="row.status === 3" class="ok">已确认</span>
            <span v-else-if="row.status === 4" class="warn">异议处理中</span>
            <span v-else class="muted">-</span>
          </template>
        </el-table-column>
      </el-table>
      <el-empty v-if="!list.length" description="暂无结算单" :image-size="72" />
    </el-card>

    <el-dialog v-model="disputeVisible" title="异议申请" width="440px">
      <el-input v-model="reason" type="textarea" :rows="4" placeholder="请说明异议原因，便于平台复核" />
      <template #footer>
        <el-button @click="disputeVisible = false">取消</el-button>
        <el-button type="primary" :loading="acting" @click="submitDispute">提交</el-button>
      </template>
    </el-dialog>
  </div>
</template>

<script setup lang="ts">
import { confirmPortalSettle, disputePortalSettle, getPortalSettles } from '@/api/agent/portal'
import { DICT_TYPE } from '@/utils/dict'
import { ElMessage, ElMessageBox } from 'element-plus'

defineOptions({ name: 'AgentPortalSettle' })

const loading = ref(false)
const acting = ref(false)
const list = ref<any[]>([])
const disputeVisible = ref(false)
const reason = ref('')
const currentId = ref<number>()

const fen2yuan = (fen?: number) =>
  fen == null
    ? '0'
    : (Number(fen) / 100).toLocaleString('zh-CN', { minimumFractionDigits: 0, maximumFractionDigits: 2 })

const kpis = computed(() => {
  const wait = list.value.filter((i) => i.status === 2)
  const confirmed = list.value.filter((i) => i.status === 3)
  const waitAmt = wait.reduce((s, i) => s + (i.totalAmount || 0), 0)
  const confirmedAmt = confirmed.reduce((s, i) => s + (i.totalAmount || 0), 0)
  return [
    { label: '结算单总数', value: String(list.value.length) },
    { label: '待确认到账', value: wait.length ? `¥${fen2yuan(waitAmt)}（${wait.length}）` : '¥0' },
    { label: '已确认', value: `¥${fen2yuan(confirmedAmt)}` }
  ]
})

const load = async () => {
  loading.value = true
  try {
    list.value = (await getPortalSettles()) || []
  } finally {
    loading.value = false
  }
}

const confirm = async (row: any) => {
  await ElMessageBox.confirm('确认已收到该笔打款？', '确认到账')
  await confirmPortalSettle(row.id)
  ElMessage.success('已确认')
  await load()
}

const openDispute = (row: any) => {
  currentId.value = row.id
  reason.value = ''
  disputeVisible.value = true
}

const submitDispute = async () => {
  if (!reason.value) return ElMessage.warning('请填写原因')
  acting.value = true
  try {
    await disputePortalSettle({ settlementId: currentId.value!, reason: reason.value })
    ElMessage.success('异议已提交')
    disputeVisible.value = false
    await load()
  } finally {
    acting.value = false
  }
}

onMounted(load)
</script>

<style scoped>
.kpi {
  background: #fff;
  border: 1px solid var(--el-border-color-lighter);
  border-radius: 10px;
  padding: 14px 16px;
  margin-bottom: 8px;
  min-height: 72px;
}
.kpi__value {
  font-size: 20px;
  font-weight: 700;
  color: #1f2937;
}
.kpi__label {
  margin-top: 4px;
  font-size: 12px;
  color: #909399;
}
.panel-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
}
.profit {
  color: #0e9c8d;
  font-weight: 700;
}
.ok {
  color: #16a34a;
  font-size: 13px;
}
.warn {
  color: #dc2626;
  font-size: 13px;
}
.muted {
  color: #909399;
}
</style>
